import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPatientEncounters, fetchAllEncounters, setEncounters } from '../../redux/slices/encountersSlice';
import { fetchPatients } from '../../redux/slices/patientsSlice';

import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Checkbox,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  TableSortLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
// Encounter Type Icons
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'; // Inpatient
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'; // Outpatient
import EmergencyIcon from '@mui/icons-material/Emergency'; // Emergency (using LocalHospital as fallback)
import VideocamIcon from '@mui/icons-material/Videocam'; // Telehealth
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'; // Observation
import HealingIcon from '@mui/icons-material/Healing'; // Surgery
import HomeIcon from '@mui/icons-material/Home'; // Home Health
import Tooltip from '@mui/material/Tooltip';
import { format, subDays, isWithinInterval, parseISO } from 'date-fns';
import { jsPDF } from 'jspdf';
import { safeFormatDate } from '../../utils/dateUtils';
import { EncounterStatus, isValidEnumValue, EncounterType } from '../../types/enums';
import { useFacilityChangeListener } from '../../hooks/useFacilityChangeListener';
import { getEncounterHistory } from '../../services/encounterHistoryService';
import ClinicalDataService from '../../services/clinicalDataService';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { useUserData } from '../../hooks/useUserData';
import { DatePicker } from '@mui/x-date-pickers';

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const StyledTableContainer = styled(Paper)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: theme.palette.grey[50],
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 1.5),
  fontSize: '0.8rem',
}));





const ActionButton = styled(IconButton)(({ theme }) => ({
  borderRadius: '8px',
  backgroundColor: theme.palette.primary.light,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

const STATUS_DISPLAY = {
  [EncounterStatus.IN_PROGRESS]: { label: 'In Progress', color: 'info' },
  [EncounterStatus.COMPLETED]: { label: 'Completed', color: 'success' },
  [EncounterStatus.TRANSFERRED]: { label: 'Transferred', color: 'primary' },
  [EncounterStatus.DISCHARGED]: { label: 'Discharged', color: 'secondary' },
  [EncounterStatus.CANCELLED]: { label: 'Cancelled', color: 'error' },
};

function renderStatusChip(status) {
  if (!status) {
    return <Chip label="Unknown" color="warning" size="small" sx={{ borderRadius: '8px', fontWeight: 500 }} />;
  }
  
  // Debug logging to help identify status value issues
  if (process.env.NODE_ENV === 'development') {
    console.log('EncounterList: Processing status:', status, 'Type:', typeof status);
  }
  
  // First try to validate against the enum
  if (isValidEnumValue(EncounterStatus, status)) {
    const { label, color } = STATUS_DISPLAY[status];
    return <Chip label={label} color={color} size="small" sx={{ borderRadius: '8px', fontWeight: 500 }} />;
  }
  
  // Handle legacy or inconsistent status values
  const normalizedStatus = status.toLowerCase().replace(/[_-]/g, '-');
  
  // Try to match normalized status
  if (isValidEnumValue(EncounterStatus, normalizedStatus)) {
    const { label, color } = STATUS_DISPLAY[normalizedStatus];
    return <Chip label={label} color={color} size="small" sx={{ borderRadius: '8px', fontWeight: 500 }} />;
  }
  
  // Handle common variations
  const statusVariations = {
    'inprogress': 'in-progress',
    'in_progress': 'in-progress',
    'active': 'in-progress',
    'admitted': 'in-progress',
    'finished': 'completed',
    'done': 'completed',
    'discharge': 'discharged',
    'cancel': 'cancelled'
  };
  
  const mappedStatus = statusVariations[status.toLowerCase()];
  if (mappedStatus && isValidEnumValue(EncounterStatus, mappedStatus)) {
    const { label, color } = STATUS_DISPLAY[mappedStatus];
    return <Chip label={label} color={color} size="small" sx={{ borderRadius: '8px', fontWeight: 500 }} />;
  }
  
  // If all else fails, show the raw status value with a warning color
  if (process.env.NODE_ENV === 'development') {
    console.warn('EncounterList: Unrecognized status value:', status, 'Available enum values:', Object.values(EncounterStatus));
  }
  return <Chip label={status} color="warning" size="small" sx={{ borderRadius: '8px', fontWeight: 500 }} />;
}

// Encounter type icon and label mapping
const ENCOUNTER_TYPE_CONFIG = {
  [EncounterType.INPATIENT_ADMISSION]: { 
    icon: LocalHospitalIcon, 
    label: 'Inpatient', 
    color: '#1976d2' // blue
  },
  [EncounterType.OUTPATIENT_VISIT]: { 
    icon: DirectionsWalkIcon, 
    label: 'Outpatient', 
    color: '#2e7d32' // green
  },
  [EncounterType.EMERGENCY_VISIT]: { 
    icon: EmergencyIcon, 
    label: 'Emergency', 
    color: '#d32f2f' // red
  },
  [EncounterType.TELEHEALTH]: { 
    icon: VideocamIcon, 
    label: 'Telehealth', 
    color: '#7b1fa2' // purple
  },
  [EncounterType.OBSERVATION]: { 
    icon: VisibilityOutlinedIcon, 
    label: 'Observation', 
    color: '#f57c00' // orange
  },
  [EncounterType.SURGERY]: { 
    icon: HealingIcon, 
    label: 'Surgery', 
    color: '#c62828' // dark red
  },
  [EncounterType.HOME_HEALTH]: { 
    icon: HomeIcon, 
    label: 'Home Health', 
    color: '#00838f' // teal
  },
};

// Helper function to render encounter type icon
function renderEncounterTypeIcon(type) {
  if (!type) {
    return null;
  }
  
  // Normalize type value (handle variations)
  const normalizedType = type.toLowerCase().replace(/[_-\s]/g, '');
  
  // Map variations to enum values
  const typeMapping = {
    'inpatient': EncounterType.INPATIENT_ADMISSION,
    'inpatientadmission': EncounterType.INPATIENT_ADMISSION,
    'outpatient': EncounterType.OUTPATIENT_VISIT,
    'outpatientvisit': EncounterType.OUTPATIENT_VISIT,
    'emergency': EncounterType.EMERGENCY_VISIT,
    'emergencyvisit': EncounterType.EMERGENCY_VISIT,
    'telehealth': EncounterType.TELEHEALTH,
    'observation': EncounterType.OBSERVATION,
    'surgery': EncounterType.SURGERY,
    'homehealth': EncounterType.HOME_HEALTH,
  };
  
  const mappedType = typeMapping[normalizedType] || type;
  const config = ENCOUNTER_TYPE_CONFIG[mappedType];
  
  if (!config) {
    // Return a default icon for unknown types
    return (
      <Tooltip title={type} arrow>
        <LocalHospitalIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
      </Tooltip>
    );
  }
  
  const IconComponent = config.icon;
  return (
    <Tooltip title={config.label} arrow>
      <IconComponent sx={{ fontSize: '1.2rem', color: config.color }} />
    </Tooltip>
  );
}

const EncounterList = ({ patientId: propPatientId, embedded = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { patientId: urlPatientId } = useParams();
  const patientId = propPatientId || urlPatientId;
  const isAllEncounters = !patientId;
  
  // Use the new useUserData hook for consistent data structure - must be called first
  const { user, defaultFacility } = useUserData();
  
  // Safety check: Filter out corrupted encounter data before using it
  const rawEncounters = useSelector(state => state.encounters.encounters);
  const { loading, error } = useSelector(state => state.encounters);
  
  // Clean encounters data by filtering out any corrupted Firestore timestamp objects
  const encounters = useMemo(() => {
    if (!rawEncounters || !Array.isArray(rawEncounters)) {
      return [];
    }
    
    try {
      const cleanEncounters = rawEncounters.filter(encounter => {
        if (!encounter) return false;
        
        // Check for corrupted timestamp fields
        const timestampFields = ['createdAt', 'updatedAt', 'startDate', 'endDate', 'checkInTime', 'dischargeTime'];
        for (const field of timestampFields) {
          if (encounter[field] && typeof encounter[field] === 'object' && encounter[field]._seconds !== undefined) {
            return false;
          }
        }
        return true;
      });
      
      return cleanEncounters;
    } catch (error) {
      console.error('EncounterList: Error cleaning encounter data:', error);
      return [];
    }
  }, [rawEncounters]);
  
  const { patients } = useSelector(state => state.patients);
  
  // Only use recent searches when we have a facility
  const { navigateToPatientById } = useRecentSearches(defaultFacility?.id || null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEncounters, setSelectedEncounters] = useState([]);
  const [patientDialogOpen, setPatientDialogOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [filterType, setFilterType] = useState('active');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sortColumn, setSortColumn] = useState('updatedAt'); // Default sort by last updated
  const [sortDirection, setSortDirection] = useState('desc'); // Default descending (most recent first)

  // Add ref to prevent multiple simultaneous data fetches
  const isFetchingRef = useRef(false);
  const hasClearedCorruptedDataRef = useRef(false);
  const mountCountRef = useRef(0);

  // Track component mounts for debugging
  useEffect(() => {
    mountCountRef.current += 1;
    
    return () => {
      // Component unmounting
    };
  }, []);

  // Get current facility ID - only show encounters for the user's default facility
  const getCurrentFacilityId = useCallback(() => {
    if (!defaultFacility?.id) {
      console.warn('No default facility set for user');
      return null;
    }
    // defaultFacility is now the complete facility object, extract the ID
    return defaultFacility.id;
  }, [defaultFacility]);

  // Listen for facility changes and refresh encounters data
  const handleFacilityChange = useCallback((newFacilityId, previousFacilityId) => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }
    
    // Use the newFacilityId parameter directly instead of calling getCurrentFacilityId()
    if (newFacilityId) {
      // Refresh encounters when facility changes
      if (isAllEncounters) {
        // Show encounters for the new facility
        dispatch(fetchAllEncounters([newFacilityId]));
        dispatch(fetchPatients(newFacilityId));
      } else {
        // For specific patient encounters, filter by user's facility for data isolation
        if (patientId) {
          dispatch(fetchPatientEncounters(patientId, newFacilityId));
        }
      }
    }
  }, [isAllEncounters, patientId, dispatch]);

  useFacilityChangeListener(handleFacilityChange);

  // Separate effect for handling corrupted data cleanup
  useEffect(() => {
    // Only run this effect once per component mount to prevent infinite loops
    if (hasClearedCorruptedDataRef.current) {
      return;
    }
    
    // Don't run corrupted data cleanup if there are no encounters yet
    // This prevents clearing encounters before they're fetched
    if (!rawEncounters || !Array.isArray(rawEncounters) || rawEncounters.length === 0) {
      return;
    }
    
    try {
      // Check if there are any corrupted encounters in the current state
      const hasCorruptedData = rawEncounters.some(encounter => {
        if (!encounter) return false;
        const timestampFields = ['createdAt', 'updatedAt', 'startDate', 'endDate', 'checkInTime', 'dischargeTime'];
        return timestampFields.some(field => 
          encounter[field] && typeof encounter[field] === 'object' && encounter[field]._seconds !== undefined
        );
      });
      
      if (hasCorruptedData) {
        dispatch(setEncounters([]));
        hasClearedCorruptedDataRef.current = true;
      }
    } catch (error) {
      console.error('EncounterList: Error checking for corrupted data:', error);
      // If there's an error checking, clear encounters as a safety measure
      dispatch(setEncounters([]));
      hasClearedCorruptedDataRef.current = true;
    }
  }, [dispatch, rawEncounters]); // Include dependencies to avoid linting warnings

  // Main data fetching effect - only runs when user or facility changes
  useEffect(() => {
    // Prevent multiple simultaneous fetches
    if (isFetchingRef.current) {
      return;
    }
    
    // Only proceed with normal data fetching if user exists
    if (user) {
      isFetchingRef.current = true;
      
      try {
        if (isAllEncounters) {
          // Show encounters for the current default facility only
          const facilityId = getCurrentFacilityId();
          if (facilityId) {
            dispatch(fetchAllEncounters([facilityId]));
            dispatch(fetchPatients(facilityId));
          } else {
            // No default facility, show empty list
            dispatch(setEncounters([]));
          }
        } else {
          // For specific patient encounters, filter by user's facility for data isolation
          const facilityId = getCurrentFacilityId();
          dispatch(fetchPatientEncounters(patientId, facilityId));
        }
      } finally {
        // Reset the fetching flag after a short delay to allow Redux to process
        setTimeout(() => {
          isFetchingRef.current = false;
        }, 100);
      }
    }
  }, [dispatch, patientId, user, user?.defaultFacility, isAllEncounters, defaultFacility, getCurrentFacilityId]);

  const enrichedEncounters = useMemo(() => {
    // Final safety check: ensure encounters is clean and safe to process
    if (!encounters || !Array.isArray(encounters)) {
      console.warn('EncounterList: encounters is not an array or is null/undefined:', encounters);
      return [];
    }
    
    // Additional safety check: filter out any remaining corrupted data
    const safeEncounters = encounters.filter(encounter => {
      if (!encounter) return false;
      
      try {
        // Check for corrupted timestamp fields
        const timestampFields = ['createdAt', 'updatedAt', 'startDate', 'endDate', 'checkInTime', 'dischargeTime'];
        for (const field of timestampFields) {
          if (encounter[field] && typeof encounter[field] === 'object' && encounter[field]._seconds !== undefined) {
            console.warn('EncounterList: Found corrupted encounter data in enrichedEncounters:', encounter);
            return false;
          }
        }
        return true;
      } catch (error) {
        console.warn('EncounterList: Error checking encounter data:', error, encounter);
        return false;
      }
    });
    
            if (safeEncounters.length !== encounters.length) {
          // Filtered out corrupted encounters
        }
    
    // Ensure both encounters and patients are arrays before processing
    if (!Array.isArray(safeEncounters)) {
      console.warn('EncounterList: safeEncounters is not an array:', safeEncounters);
      return [];
    }
    
    // Convert patients object to array if needed
    let patientsArray = patients;
    if (patients && typeof patients === 'object' && !Array.isArray(patients)) {
      patientsArray = Object.values(patients);
    }
    
    if (!Array.isArray(patientsArray)) {
      console.warn('EncounterList: patients is not an array:', patientsArray);
      return safeEncounters;
    }
    
    if (isAllEncounters) {
      try {
        const patientMap = new Map(patientsArray.map(p => [p.id, p]));
        return safeEncounters.map(v => {
          // First, check if backend already provided patientName (preferred)
          if (v.patientName && v.patientName !== 'Unknown Patient') {
            // Backend already enriched this encounter, use those values
            return {
              ...v,
              mrn: v.mrn || 'N/A',
            };
          }
          
          // Fallback: try to get patient name from frontend state
          const patient = patientMap.get(v.patientId);
          let patientName = 'Unknown Patient';
          
          if (patient) {
            // Handle different patient name structures
            if (patient.firstName && patient.lastName) {
              patientName = `${patient.firstName} ${patient.lastName}`;
            } else if (patient.name) {
              patientName = patient.name;
            } else if (patient.firstName) {
              patientName = patient.firstName;
            } else if (patient.lastName) {
              patientName = patient.lastName;
            }
          }
          
          return {
            ...v,
            patientName,
            mrn: patient?.mrn || v.mrn || 'N/A',
          };
        });
      } catch (error) {
        console.error('EncounterList: Error enriching encounters:', error);
        return safeEncounters;
      }
    }
    
    return safeEncounters;
  }, [encounters, patients, isAllEncounters]);

  // Filter encounters based on selected filter type
  const filteredEncounters = useMemo(() => {
    // Ensure enrichedEncounters is an array before filtering
    if (!Array.isArray(enrichedEncounters)) {
      return [];
    }
    
    let filtered = enrichedEncounters;

    // Apply filter type
    switch (filterType) {
      case 'active':
        filtered = filtered.filter(encounter => 
          encounter.status === EncounterStatus.IN_PROGRESS || encounter.status === EncounterStatus.ACTIVE
        );
        break;
      case 'discharged-30-days':
        const thirtyDaysAgo = subDays(new Date(), 30);
        filtered = filtered.filter(encounter => {
          if (encounter.status !== EncounterStatus.DISCHARGED && encounter.status !== EncounterStatus.COMPLETED) {
            return false;
          }
          const dischargeDate = encounter.endDate || encounter.updatedAt || encounter.createdAt;
          if (!dischargeDate) return false;
          
          const date = typeof dischargeDate === 'string' ? parseISO(dischargeDate) : dischargeDate.toDate ? dischargeDate.toDate() : new Date(dischargeDate);
          return date >= thirtyDaysAgo;
        });
        break;
      case 'date-range':
        if (startDate && endDate) {
          const start = parseISO(startDate);
          const end = parseISO(endDate);
          filtered = filtered.filter(encounter => {
            const admissionDate = encounter.createdAt || encounter.startDate;
            if (!admissionDate) return false;
            
            const date = typeof admissionDate === 'string' ? parseISO(admissionDate) : admissionDate.toDate ? admissionDate.toDate() : new Date(admissionDate);
            return isWithinInterval(date, { start, end });
          });
        }
        break;
      default:
        break;
    }

    // Apply search filter
    if (searchTerm && typeof searchTerm === 'string') {
      const searchString = searchTerm.toLowerCase();
      filtered = filtered.filter(encounter => (
        (encounter.notes?.consultationNotes && typeof encounter.notes.consultationNotes === 'string' && encounter.notes.consultationNotes.toLowerCase().includes(searchString)) ||
        (Array.isArray(encounter.symptoms) && encounter.symptoms.some(symptom => (symptom && typeof symptom === 'string' && symptom.toLowerCase().includes(searchString)))) ||
        (encounter.notes?.soapNotes?.assessment && typeof encounter.notes.soapNotes.assessment === 'string' && encounter.notes.soapNotes.assessment.toLowerCase().includes(searchString)) ||
        (encounter.actionPlan && typeof encounter.actionPlan === 'string' && encounter.actionPlan.toLowerCase().includes(searchString)) ||
        (encounter.provider?.name && typeof encounter.provider.name === 'string' && encounter.provider.name.toLowerCase().includes(searchString)) ||
        (encounter.provider?.role && typeof encounter.provider.role === 'string' && encounter.provider.role.toLowerCase().includes(searchString)) ||
        (isAllEncounters && encounter.patientName && typeof encounter.patientName === 'string' && encounter.patientName.toLowerCase().includes(searchString)) ||
        (isAllEncounters && encounter.mrn && typeof encounter.mrn === 'string' && encounter.mrn.toLowerCase().includes(searchString))
      ));
    }

    return filtered;
  }, [enrichedEncounters, filterType, startDate, endDate, searchTerm, isAllEncounters]);

  // Sort encounters based on current sort column and direction
  const sortedEncounters = useMemo(() => {
    if (!Array.isArray(filteredEncounters) || filteredEncounters.length === 0) {
      return [];
    }

    const sorted = [...filteredEncounters].sort((a, b) => {
      let aValue, bValue;

      switch (sortColumn) {
        case 'patientName':
          aValue = (a.patientName || '').toLowerCase();
          bValue = (b.patientName || '').toLowerCase();
          break;
        case 'createdAt':
        case 'updatedAt':
          // Parse dates for comparison
          const getDateValue = (encounter, field) => {
            const dateField = encounter[field] || encounter.createdAt || encounter.startDate;
            if (!dateField) return 0;
            
            if (typeof dateField === 'string') {
              return new Date(dateField).getTime();
            } else if (dateField instanceof Date) {
              return dateField.getTime();
            } else if (dateField.seconds) {
              return dateField.seconds * 1000;
            } else if (dateField._seconds) {
              return dateField._seconds * 1000;
            } else if (dateField.toDate) {
              return dateField.toDate().getTime();
            }
            return 0;
          };
          aValue = getDateValue(a, sortColumn);
          bValue = getDateValue(b, sortColumn);
          break;
        default:
          aValue = a[sortColumn] || '';
          bValue = b[sortColumn] || '';
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric/date comparison
      if (sortDirection === 'asc') {
        return aValue - bValue;
      }
      return bValue - aValue;
    });

    return sorted;
  }, [filteredEncounters, sortColumn, sortDirection]);

  // Handle sort column click
  const handleSortClick = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column with default descending for dates, ascending for text
      setSortColumn(column);
      setSortDirection(column === 'patientName' ? 'asc' : 'desc');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSelectEncounter = (encounterId) => {
    setSelectedEncounters((prevSelected) => {
      // Ensure prevSelected is always an array
      if (!Array.isArray(prevSelected)) {
        console.warn('EncounterList: prevSelected is not an array, resetting to empty array');
        return [encounterId];
      }
      
      return prevSelected.includes(encounterId) 
        ? prevSelected.filter(id => id !== encounterId) 
        : [...prevSelected, encounterId];
    });
  };

  const handleExportSelectedRows = async () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPos = 20;
      const margin = 20;
      const usableHeight = pageHeight - 40; // Leave margins

      // Helper function to add new page if needed
      const checkAndAddPage = (neededHeight = 15) => {
        if (yPos + neededHeight > usableHeight) {
          doc.addPage();
          yPos = 20;
        }
      };

      // Helper function to wrap text
      const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
        doc.setFontSize(fontSize);
        const lines = doc.splitTextToSize(text || '', maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.5); // Return height used
      };

      // Add main header
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Patient Encounter Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${format(new Date(), 'MMM dd, yyyy HH:mm')}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 20;

      // Process each selected encounter
      if (!Array.isArray(selectedEncounters) || selectedEncounters.length === 0) {
        console.warn('EncounterList: No encounters selected for export');
        return;
      }
      
      for (let i = 0; i < selectedEncounters.length; i++) {
        const encounterId = selectedEncounters[i];
        const encounter = Array.isArray(enrichedEncounters) ? enrichedEncounters.find(e => e.id === encounterId) : null;
        
        if (!encounter) continue;

        // Add thick separator line between encounters (except for first)
        if (i > 0) {
          checkAndAddPage(25);
          doc.setLineWidth(2);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 15;
        }

        // ENCOUNTER HEADER SECTION
        checkAndAddPage(40);
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text(`Encounter ${i + 1}: ${(encounter.type && typeof encounter.type === 'string' && encounter.type === EncounterType.HOME_HEALTH) ? 'Home Health' : (encounter.type && typeof encounter.type === 'string') ? encounter.type : 'General'} Encounter`, margin, yPos);
        yPos += 10;

        // Patient and encounter info in two columns
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        
        const leftCol = margin;
        const rightCol = pageWidth / 2 + 10;
        const startY = yPos;
        
        // Left column
        doc.text(`Patient: ${(encounter.patientName && typeof encounter.patientName === 'string') ? encounter.patientName : 'Unknown'}`, leftCol, yPos);
        yPos += 5;
        doc.text(`MRN: ${(encounter.mrn && typeof encounter.mrn === 'string') ? encounter.mrn : 'N/A'}`, leftCol, yPos);
        yPos += 5;
        doc.text(`FIN: ${(encounter.fin && typeof encounter.fin === 'string') ? encounter.fin : 'N/A'}`, leftCol, yPos);
        yPos += 5;
        doc.text(`Date: ${safeFormatDate(encounter.createdAt)}`, leftCol, yPos);
        yPos += 5;
        doc.text(`Status: ${(encounter.status && typeof encounter.status === 'string') ? (encounter.status === EncounterStatus.DISCHARGED ? 'Discharged' : encounter.status === EncounterStatus.COMPLETED ? 'Completed' : encounter.status === EncounterStatus.IN_PROGRESS ? 'In Progress' : encounter.status === EncounterStatus.TRANSFERRED ? 'Transferred' : encounter.status === EncounterStatus.CANCELLED ? 'Cancelled' : 'Unknown') : 'Unknown'}`, leftCol, yPos);

        // Right column
        yPos = startY;
        doc.text(`Department: ${(encounter.department && typeof encounter.department === 'string') ? encounter.department : 'N/A'}`, rightCol, yPos);
        yPos += 5;
        doc.text(`Room: ${(encounter.room && typeof encounter.room === 'string') ? encounter.room : (encounter.roomName && typeof encounter.roomName === 'string') ? encounter.roomName : 'N/A'}`, rightCol, yPos);
        yPos += 5;
        doc.text(`Bed: ${(encounter.bed && typeof encounter.bed === 'string') ? encounter.bed : (encounter.bedName && typeof encounter.bedName === 'string') ? encounter.bedName : 'N/A'}`, rightCol, yPos);
        yPos += 5;
        doc.text(`Attending Physician: ${getAttendingPhysician(encounter) || 'N/A'}`, rightCol, yPos);
        yPos += 5;
        doc.text(`Stay Duration: ${calculateStayDuration(encounter) || 'N/A'}`, rightCol, yPos);
        
        yPos += 15;

        // CLINICAL NOTES SECTION
        checkAndAddPage(30);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Clinical Notes & History:', margin, yPos);
        yPos += 8;

        // Fetch encounter history
        try {
          const encounterHistory = await getEncounterHistory(encounterId, 100);
          
          if (encounterHistory && Array.isArray(encounterHistory) && encounterHistory.length > 0) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            
            // Group and display notes from history
            for (let index = 0; index < encounterHistory.length; index++) {
              const historyEntry = encounterHistory[index];
              checkAndAddPage(20);
              
              const entryDate = format(new Date(historyEntry.changedAt), 'MMM dd, yyyy HH:mm');
              doc.setFont('helvetica', 'bold');
              doc.text(`Entry ${index + 1} - ${entryDate}`, margin, yPos);
              yPos += 5;
              doc.setFont('helvetica', 'normal');
              
              const notes = historyEntry.clinicalData?.notes || {};
              
              // Display different types of notes
              const noteEntries = Object.entries(notes);
              if (Array.isArray(noteEntries)) {
                for (let noteIndex = 0; noteIndex < noteEntries.length; noteIndex++) {
                const [noteType, noteContent] = noteEntries[noteIndex];
                if (noteContent && typeof noteContent === 'string' && noteContent.trim()) {
                  checkAndAddPage(15);
                  doc.setFont('helvetica', 'bold');
                  const formattedNoteType = typeof noteType === 'string' ? noteType.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()) : 'Unknown';
                  doc.text(`${formattedNoteType}:`, margin + 5, yPos);
                  yPos += 4;
                  doc.setFont('helvetica', 'normal');
                  const textHeight = addWrappedText(noteContent, margin + 5, yPos, pageWidth - margin - 30, 9);
                  yPos += textHeight + 3;
                } else if (noteType === 'soapNotes' && typeof noteContent === 'object') {
                  // Handle SOAP notes specially
                  const soapEntries = Object.entries(noteContent);
                  if (Array.isArray(soapEntries)) {
                    for (let soapIndex = 0; soapIndex < soapEntries.length; soapIndex++) {
                    const [soapType, soapText] = soapEntries[soapIndex];
                    if (soapText && typeof soapText === 'string' && soapText.trim()) {
                      checkAndAddPage(10);
                      doc.setFont('helvetica', 'bold');
                      doc.text(`${typeof soapType === 'string' ? soapType.toUpperCase() : 'UNKNOWN'}:`, margin + 5, yPos);
                      yPos += 4;
                      doc.setFont('helvetica', 'normal');
                      const textHeight = addWrappedText(soapText, margin + 5, yPos, pageWidth - margin - 30, 9);
                      yPos += textHeight + 3;
                    }
                  }
                  } // Close the if (Array.isArray(soapEntries)) block
                }
              }
              } // Close the if (Array.isArray(noteEntries)) block
              
              // Add other clinical data
              if (historyEntry.clinicalData?.chiefComplaint && typeof historyEntry.clinicalData.chiefComplaint === 'string') {
                checkAndAddPage(10);
                doc.setFont('helvetica', 'bold');
                doc.text('Chief Complaint:', margin + 5, yPos);
                yPos += 4;
                doc.setFont('helvetica', 'normal');
                const textHeight = addWrappedText(historyEntry.clinicalData.chiefComplaint, margin + 5, yPos, pageWidth - margin - 30, 9);
                yPos += textHeight + 3;
              }
              
              if (historyEntry.clinicalData?.actionPlan && typeof historyEntry.clinicalData.actionPlan === 'string') {
                checkAndAddPage(10);
                doc.setFont('helvetica', 'bold');
                doc.text('Action Plan:', margin + 5, yPos);
                yPos += 4;
                doc.setFont('helvetica', 'normal');
                const textHeight = addWrappedText(historyEntry.clinicalData.actionPlan, margin + 5, yPos, pageWidth - margin - 30, 9);
                yPos += textHeight + 3;
              }
              
              yPos += 5; // Space between history entries
            }
          } else {
            doc.setFont('helvetica', 'italic');
            doc.text('No clinical notes found in encounter history.', margin + 5, yPos);
            yPos += 10;
          }
        } catch (error) {
          console.error('Error fetching encounter history:', error);
          doc.setFont('helvetica', 'italic');
          doc.text('Error loading encounter history.', margin + 5, yPos);
          yPos += 10;
        }

        // PRESCRIPTIONS SECTION
        checkAndAddPage(25);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Prescriptions:', margin, yPos);
        yPos += 8;

        try {
          // Fetch prescriptions for this patient/encounter using the service
          const clinicalDataService = new ClinicalDataService();
          const prescriptions = await clinicalDataService.getPatientPrescriptions(encounter.patientId, encounterId, 100);
          
          // Filter prescriptions related to this encounter
          const encounterPrescriptions = Array.isArray(prescriptions) ? prescriptions.filter(p => 
            p.encounterId === encounterId || 
            (p.createdAt && encounter.createdAt && 
             Math.abs(new Date(p.createdAt.seconds * 1000) - new Date(encounter.createdAt.seconds * 1000)) < 24 * 60 * 60 * 1000) // Within 24 hours
          ) : [];

          if (Array.isArray(encounterPrescriptions) && encounterPrescriptions.length > 0) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            
            for (let index = 0; index < encounterPrescriptions.length; index++) {
              const prescription = encounterPrescriptions[index];
              checkAndAddPage(15);
              doc.setFont('helvetica', 'bold');
              doc.text(`Prescription ${index + 1}:`, margin + 5, yPos);
              yPos += 5;
              doc.setFont('helvetica', 'normal');
              
              if (prescription.medications && Array.isArray(prescription.medications)) {
                for (let medIndex = 0; medIndex < prescription.medications.length; medIndex++) {
                  const med = prescription.medications[medIndex];
                  checkAndAddPage(10);
                  doc.text(`• ${(med.name && typeof med.name === 'string') ? med.name : 'Unknown medication'}`, margin + 10, yPos);
                  yPos += 4;
                  doc.text(`  Dosage: ${(med.dosage && typeof med.dosage === 'string') ? med.dosage : 'N/A'} | Frequency: ${(med.frequency && typeof med.frequency === 'string') ? med.frequency : 'N/A'}`, margin + 10, yPos);
                  yPos += 4;
                  if (med.instructions && typeof med.instructions === 'string') {
                    const instrHeight = addWrappedText(`  Instructions: ${med.instructions}`, margin + 10, yPos, pageWidth - margin - 40, 9);
                    yPos += instrHeight + 2;
                  }
                }
              } else {
                doc.text(`• ${(prescription.medication && typeof prescription.medication === 'string') ? prescription.medication : (prescription.medicationName && typeof prescription.medicationName === 'string') ? prescription.medicationName : 'Unknown medication'}`, margin + 10, yPos);
                yPos += 4;
                doc.text(`  Dosage: ${(prescription.dosage && typeof prescription.dosage === 'string') ? prescription.dosage : 'N/A'} | Frequency: ${(prescription.frequency && typeof prescription.frequency === 'string') ? prescription.frequency : 'N/A'}`, margin + 10, yPos);
                yPos += 4;
              }
              yPos += 3;
            }
          } else {
            doc.setFont('helvetica', 'italic');
            doc.text('No prescriptions found for this encounter.', margin + 5, yPos);
            yPos += 10;
          }
        } catch (error) {
          console.error('Error fetching prescriptions:', error);
          doc.setFont('helvetica', 'italic');
          doc.text('Error loading prescriptions.', margin + 5, yPos);
          yPos += 10;
        }

        // ORDERS SECTION
        checkAndAddPage(25);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Orders & Procedures:', margin, yPos);
        yPos += 8;

        try {
          // Fetch orders/procedures for this patient/encounter using the service
          const clinicalDataService = new ClinicalDataService();
          const orders = await clinicalDataService.getPatientProcedures(encounter.patientId, encounterId, 100);
          
          // Filter orders related to this encounter
          const encounterOrders = Array.isArray(orders) ? orders.filter(o => 
            o.encounterId === encounterId ||
            (o.orderDate && encounter.createdAt && 
             Math.abs(new Date(o.orderDate.seconds * 1000) - new Date(encounter.createdAt.seconds * 1000)) < 24 * 60 * 60 * 1000) // Within 24 hours
          ) : [];

          if (Array.isArray(encounterOrders) && encounterOrders.length > 0) {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            
            for (let index = 0; index < encounterOrders.length; index++) {
              const order = encounterOrders[index];
              checkAndAddPage(12);
              doc.setFont('helvetica', 'bold');
              doc.text(`Order ${index + 1}:`, margin + 5, yPos);
              yPos += 5;
              doc.setFont('helvetica', 'normal');
              
              doc.text(`• ${(order.description && typeof order.description === 'string') ? order.description : (order.testType && typeof order.testType === 'string') ? order.testType : 'Unknown order'}`, margin + 10, yPos);
              yPos += 4;
              doc.text(`  Status: ${(order.status && typeof order.status === 'string') ? (order.status === EncounterStatus.COMPLETED ? 'Completed' : order.status === EncounterStatus.IN_PROGRESS ? 'In Progress' : 'Unknown') : 'Unknown'} | Type: ${(order.testType && typeof order.testType === 'string' && order.testType === EncounterType.HOME_HEALTH) ? 'Home Health' : (order.testType && typeof order.testType === 'string') ? order.testType : (order.orderType && typeof order.orderType === 'string') ? order.orderType : 'N/A'}`, margin + 10, yPos);
              yPos += 4;
              doc.text(`  Code: ${(order.testCode && typeof order.testCode === 'string') ? order.testCode : 'N/A'} | Provider: ${(order.orderingProvider && typeof order.orderingProvider === 'string') ? order.orderingProvider : 'N/A'}`, margin + 10, yPos);
              yPos += 4;
              
              if (order.notes && typeof order.notes === 'string') {
                const notesHeight = addWrappedText(`  Notes: ${order.notes}`, margin + 10, yPos, pageWidth - margin - 40, 9);
                yPos += notesHeight + 2;
              }
              yPos += 3;
            }
          } else {
            doc.setFont('helvetica', 'italic');
            doc.text('No orders found for this encounter.', margin + 5, yPos);
            yPos += 10;
          }
        } catch (error) {
          console.error('Error fetching orders:', error);
          doc.setFont('helvetica', 'italic');
          doc.text('Error loading orders.', margin + 5, yPos);
          yPos += 10;
        }

        yPos += 10; // Extra space before next encounter
      }

      // Save the PDF
      const fileName = `encounter_report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF report. Please try again.');
    }
  };

  const paginatedEncounters = Array.isArray(sortedEncounters) ? sortedEncounters.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  ).filter(Boolean) : []; // Filter out any null/undefined encounters
  


  // Helper function to get attending physician from encounter
  const getAttendingPhysician = (encounter) => {
    // Check for attending physician in various locations
    if (encounter.attendingPhysicianName) {
      return encounter.attendingPhysicianName;
    }
    
    if (encounter.inpatientDetails?.attendingPhysician) {
      return encounter.inpatientDetails.attendingPhysician;
    }
    
    if (encounter.observationDetails?.attendingPhysician) {
      return encounter.observationDetails.attendingPhysician;
    }
    
    if (encounter.emergencyDetails?.attendingPhysician) {
      return encounter.emergencyDetails.attendingPhysician;
    }
    
    // Fallback to general provider if no attending physician
    if (encounter.provider?.name) {
      return encounter.provider.name;
    }
    
    return '';
  };

  // Helper function to calculate stay duration
  const calculateStayDuration = (encounter) => {
    try {
      // Only calculate for non-discharged encounters
      if (encounter.status === EncounterStatus.DISCHARGED || encounter.status === EncounterStatus.COMPLETED) {
        return '';
      }

      // Get admission time (use startDate, createdAt, or current time as fallback)
      const admissionTime = encounter.startDate || encounter.createdAt;
      if (!admissionTime) {
        return '';
      }

      let startTime;
      
      // Handle Firestore Timestamp objects
      if (admissionTime && typeof admissionTime === 'object' && admissionTime._seconds !== undefined && admissionTime._nanoseconds !== undefined) {
        startTime = new Date(admissionTime._seconds * 1000 + admissionTime._nanoseconds / 1000000);
      } else if (admissionTime && typeof admissionTime === 'object' && admissionTime.seconds) {
        startTime = new Date(admissionTime.seconds * 1000);
      } else if (admissionTime && typeof admissionTime === 'object' && admissionTime.toDate) {
        startTime = admissionTime.toDate();
      } else {
        startTime = new Date(admissionTime);
      }
      
      const currentTime = new Date();
      
      // Check if dates are valid
      if (isNaN(startTime.getTime()) || isNaN(currentTime.getTime())) {
        return '';
      }
      
      // Calculate difference in milliseconds
      const diffMs = currentTime - startTime;
      
      // If negative time (future date), return blank
      if (diffMs < 0) {
        return '';
      }

      // Convert to different units
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      // Return appropriate unit based on duration
      if (diffMinutes < 60) {
        return `${diffMinutes}m`;
      } else if (diffHours < 24) {
        return `${diffHours}h`;
      } else if (diffDays < 365) {
        return `${diffDays}d`;
      } else {
        // For stays longer than a year, show in days
        return `${diffDays}d`;
      }
    } catch (error) {
      console.warn('Error calculating stay duration:', error, encounter);
      return '';
    }
  };

  // Filter patients for the user's default facility
  const facilityPatients = useMemo(() => {
    if (!defaultFacility?.id) return [];
    
    // Convert patients object to array if needed
    let patientsArray = patients;
    if (patients && typeof patients === 'object' && !Array.isArray(patients)) {
      patientsArray = Object.values(patients);
    }
    
    if (!Array.isArray(patientsArray)) return [];
    
    return patientsArray.filter(
      (p) => p.facilityId === defaultFacility.id && 
             (p.name || (p.firstName && p.lastName)) && 
             p.mrn && 
             p.dateOfBirth
    );
  }, [patients, defaultFacility?.id]);

  const filteredFacilityPatients = useMemo(() => {
    if (!Array.isArray(facilityPatients)) return [];
    const search = typeof patientSearch === 'string' ? patientSearch.toLowerCase() : '';
    return facilityPatients.filter(
      (p) => {
        const patientName = p.name || (p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : '');
        return (
          (patientName && typeof patientName === 'string' && patientName.toLowerCase().includes(search)) ||
          (p.mrn && typeof p.mrn === 'string' && p.mrn.toLowerCase().includes(search)) ||
          (p.dateOfBirth && typeof p.dateOfBirth === 'string' && p.dateOfBirth.toLowerCase().includes(search))
        );
      }
    );
  }, [facilityPatients, patientSearch]);

  const handleOpenPatientDialog = () => {
    setPatientDialogOpen(true);
    setPatientSearch('');
    setSelectedPatientId(null);
  };
  const handleClosePatientDialog = () => {
    setPatientDialogOpen(false);
    setPatientSearch('');
    setSelectedPatientId(null);
  };
  const handleSelectPatient = (patientId) => {
    setSelectedPatientId(patientId);
  };
  const handleConfirmPatient = () => {
    if (selectedPatientId) {
      setPatientDialogOpen(false);
      navigate(`/patients/${selectedPatientId}/encounters/new`);
    }
  };

  // Handle patient click from encounter list
  const handlePatientClick = useCallback(async (encounter) => {
    try {
      // Try to find patient data in the patients state
      let patient = null;
      if (patients && typeof patients === 'object') {
        // If patients is an object with IDs as keys
        patient = patients[encounter.patientId];
      } else if (Array.isArray(patients)) {
        // If patients is an array
        patient = patients.find(p => p.id === encounter.patientId);
      }
      
      if (patient) {
        await navigateToPatientById(encounter.patientId, patient, navigate);
      } else {
        // If patient data not available, just navigate
        navigate(`/patients/${encounter.patientId}`);
      }
    } catch (error) {
      console.error('Error handling patient click:', error);
      // Fallback to direct navigation if recent search fails
      navigate(`/patients/${encounter.patientId}`);
    }
  }, [navigateToPatientById, navigate, patients]);

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  

  
  if (loading) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="text.secondary">Loading encounters...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6" color="error">Error loading encounters: {error}</Typography>
        </Box>
      </Box>
    );
  }


  
    // Temporarily disable early return to debug the issue
  if (!Array.isArray(encounters) || encounters.length === 0) {
    // return (
    //   ... existing early return code ...
    // );
  }

  return (
    <Box sx={{ 
      p: embedded ? 0 : 4, 
      background: embedded ? 'transparent' : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: embedded ? 'auto' : '100vh' 
    }}>
      {/* Only show header when not embedded */}
      {!embedded && (
        <HeaderCard>
          <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {isAllEncounters ? 'All Patient Encounters' : 'Patient Encounters'}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {isAllEncounters ? 'View and manage all patient encounters across the system' : 'Track patient encounters, symptoms, and treatment plans'}
                </Typography>
              </Box>
              {isAllEncounters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenPatientDialog}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                    ml: 2,
                  }}
                >
                  Create Encounter
                </Button>
              )}
              {!isAllEncounters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/patients/${patientId}/encounters/select-type`)}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  New Encounter
                </Button>
              )}
            </Box>
          </CardContent>
        </HeaderCard>
      )}

      <ModernCard sx={embedded ? { boxShadow: 'none', borderRadius: 0, mb: 0 } : {}}>
        <CardContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search encounters by symptoms, notes, action plans, or provider..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Filter Chips */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                label="Active Encounters"
                onClick={() => setFilterType('active')}
                color={filterType === 'active' ? 'primary' : 'default'}
                variant={filterType === 'active' ? 'filled' : 'outlined'}
                sx={{ borderRadius: '20px', fontWeight: 500 }}
              />
              <Chip
                label="Discharged (Last 30 Days)"
                onClick={() => setFilterType('discharged-30-days')}
                color={filterType === 'discharged-30-days' ? 'primary' : 'default'}
                variant={filterType === 'discharged-30-days' ? 'filled' : 'outlined'}
                sx={{ borderRadius: '20px', fontWeight: 500 }}
              />
              <Chip
                label="Date Range"
                onClick={() => setFilterType('date-range')}
                color={filterType === 'date-range' ? 'primary' : 'default'}
                variant={filterType === 'date-range' ? 'filled' : 'outlined'}
                sx={{ borderRadius: '20px', fontWeight: 500 }}
              />
              
              {/* Date Range Inputs */}
              {filterType === 'date-range' && (
                <Stack direction="row" spacing={2} alignItems="center">
                  <DatePicker
                    label="Start Date"
                    value={startDate ? new Date(startDate) : null}
                    onChange={(date) => setStartDate(date ? date.toISOString().split('T')[0] : '')}
                    slotProps={{
                      textField: { size: 'small', sx: { minWidth: 150 } }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    to
                  </Typography>
                  <DatePicker
                    label="End Date"
                    value={endDate ? new Date(endDate) : null}
                    onChange={(date) => setEndDate(date ? date.toISOString().split('T')[0] : '')}
                    slotProps={{
                      textField: { size: 'small', sx: { minWidth: 150 } }
                    }}
                  />
                </Stack>
              )}
            </Stack>
          </Box>

          <StyledTableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      indeterminate={Array.isArray(selectedEncounters) && selectedEncounters.length > 0 && selectedEncounters.length < (Array.isArray(sortedEncounters) ? sortedEncounters.length : 0)}
                      checked={Array.isArray(sortedEncounters) && sortedEncounters.length > 0 && Array.isArray(selectedEncounters) && selectedEncounters.length === sortedEncounters.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedEncounters(Array.isArray(sortedEncounters) ? sortedEncounters.map(encounter => encounter.id) : []);
                        } else {
                          setSelectedEncounters([]);
                        }
                      }}
                    />
                  </StyledTableCell>
                  <StyledTableCell>Actions</StyledTableCell>
                  <StyledTableCell sx={{ width: 40, minWidth: 40 }}></StyledTableCell>
                  <StyledTableCell sortDirection={sortColumn === 'updatedAt' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortColumn === 'updatedAt'}
                      direction={sortColumn === 'updatedAt' ? sortDirection : 'desc'}
                      onClick={() => handleSortClick('updatedAt')}
                      sx={{ 
                        fontWeight: 600,
                        '&.MuiTableSortLabel-root': {
                          color: 'inherit',
                        },
                        '&.Mui-active': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      Date
                    </TableSortLabel>
                  </StyledTableCell>
                  {isAllEncounters && (
                    <StyledTableCell sortDirection={sortColumn === 'patientName' ? sortDirection : false}>
                      <TableSortLabel
                        active={sortColumn === 'patientName'}
                        direction={sortColumn === 'patientName' ? sortDirection : 'asc'}
                        onClick={() => handleSortClick('patientName')}
                        sx={{ 
                          fontWeight: 600,
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                          },
                          '&.Mui-active': {
                            color: 'primary.main',
                          },
                        }}
                      >
                        Patient
                      </TableSortLabel>
                    </StyledTableCell>
                  )}
                  {isAllEncounters && <StyledTableCell>MRN</StyledTableCell>}
                  <StyledTableCell>FIN</StyledTableCell>
                  <StyledTableCell>Provider</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Room</StyledTableCell>
                  <StyledTableCell>Bed</StyledTableCell>
                  <StyledTableCell>Stay</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  if (!Array.isArray(paginatedEncounters) || paginatedEncounters.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={isAllEncounters ? 12 : 10} align="center">
                          No encounters found in paginatedEncounters
                        </TableCell>
                      </TableRow>
                    );
                  }
                  
                  return paginatedEncounters.map((encounter) => {
                  // Safety check: ensure encounter data is properly sanitized
                  const sanitizedEncounter = (() => {
                    try {
                      const sanitized = { ...encounter };
                      
                      // Handle timestamp fields - enhanced sanitization
                      const timestampFields = ['createdAt', 'updatedAt', 'startDate', 'endDate', 'checkInTime', 'dischargeTime'];
                      timestampFields.forEach(field => {
                        if (sanitized[field] && typeof sanitized[field] === 'object') {
                          // Handle corrupted Firestore timestamp objects with _seconds and _nanoseconds
                          if (sanitized[field]._seconds !== undefined && sanitized[field]._nanoseconds !== undefined) {
                            try {
                              sanitized[field] = new Date(sanitized[field]._seconds * 1000 + sanitized[field]._nanoseconds / 1000000);
                            } catch (error) {
                              console.warn('Error converting corrupted timestamp:', error, sanitized[field]);
                              sanitized[field] = null; // Set to null if conversion fails
                            }
                          } 
                          // Handle proper Firestore Timestamps
                          else if (sanitized[field].seconds && sanitized[field].nanoseconds) {
                            try {
                              sanitized[field] = new Date(sanitized[field].seconds * 1000 + sanitized[field].nanoseconds / 1000000);
                            } catch (error) {
                              console.warn('Error converting Firestore timestamp:', error, sanitized[field]);
                              sanitized[field] = null;
                            }
                          }
                          // Handle Firestore Timestamps with toDate method
                          else if (sanitized[field].toDate && typeof sanitized[field].toDate === 'function') {
                            try {
                              sanitized[field] = sanitized[field].toDate();
                            } catch (error) {
                              console.warn('Error calling toDate on timestamp:', error, sanitized[field]);
                              sanitized[field] = null;
                            }
                          }
                          // If it's an object but not a recognized timestamp format, set to null
                          else {
                            console.warn('Unknown timestamp object format:', sanitized[field]);
                            sanitized[field] = null;
                          }
                        }
                      });
                      
                      // Additional safety check: ensure all fields are safe for rendering
                      Object.keys(sanitized).forEach(key => {
                        const value = sanitized[key];
                        if (value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined) {
                          console.warn('Found additional corrupted timestamp field:', key, value);
                          try {
                            sanitized[key] = new Date(value._seconds * 1000 + value._nanoseconds / 1000000);
                          } catch {
                            sanitized[key] = null;
                          }
                        }
                      });
                      
                      return sanitized;
                    } catch (error) {
                      console.warn('Error sanitizing encounter data:', error, encounter);
                      // Return a minimal safe object if sanitization fails completely
                      return {
                        id: encounter?.id || 'unknown',
                        createdAt: null,
                        updatedAt: null,
                        startDate: null,
                        endDate: null,
                        checkInTime: null,
                        dischargeTime: null,
                        status: 'Unknown',
                        patientId: encounter?.patientId || 'unknown'
                      };
                    }
                  })();
                  
                  // Final safety check: ensure the encounter is safe to render
                  if (!sanitizedEncounter || !sanitizedEncounter.id) {
                    console.warn('Skipping encounter with invalid data:', sanitizedEncounter);
                    return null;
                  }
                  
                  // Additional check for any remaining corrupted timestamp objects
                  const hasCorruptedTimestamps = Object.values(sanitizedEncounter).some(value => 
                    value && typeof value === 'object' && value._seconds !== undefined && value._nanoseconds !== undefined
                  );
                  
                  if (hasCorruptedTimestamps) {
                    console.error('Found corrupted timestamp objects in sanitized encounter, skipping render:', sanitizedEncounter);
                    return null;
                  }
                  
                  return (
                    <TableRow 
                      key={sanitizedEncounter.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover 
                        } 
                      }}
                    >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={Array.isArray(selectedEncounters) && selectedEncounters.includes(sanitizedEncounter.id)}
                        onChange={() => handleSelectEncounter(sanitizedEncounter.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <ActionButton
                        onClick={() => {
                          // Try to find patient data in the patients state
                          const patient = patients[sanitizedEncounter.patientId];
                          if (patient) {
                            // Use existing patient data
                            navigate(`/encounters/${sanitizedEncounter.id}`);
                          } else {
                            // Fallback to patient-scoped route if no data
                            navigate(`/patients/${sanitizedEncounter.patientId}/encounters/${sanitizedEncounter.id}`);
                          }
                        }}
                        size="small"
                        sx={{ width: 28, height: 28 }}
                      >
                        <VisibilityIcon sx={{ fontSize: '1rem' }} />
                      </ActionButton>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', width: 40 }}>
                      {renderEncounterTypeIcon(sanitizedEncounter.type || sanitizedEncounter.encounterType)}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                      {safeFormatDate(sanitizedEncounter.createdAt)}
                    </TableCell>
                    {isAllEncounters && (
                      <TableCell>
                        <Button
                          variant="text"
                          color="primary"
                          onClick={() => handlePatientClick(sanitizedEncounter)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            fontSize: '0.8rem',
                            p: 0.5,
                            minWidth: 'auto'
                          }}
                        >
                          {(sanitizedEncounter.patientName && typeof sanitizedEncounter.patientName === 'string') ? sanitizedEncounter.patientName : `Patient ${sanitizedEncounter.patientId}`}
                        </Button>
                      </TableCell>
                    )}
                    {isAllEncounters && <TableCell sx={{ fontSize: '0.8rem' }}>{(sanitizedEncounter.mrn && typeof sanitizedEncounter.mrn === 'string') ? sanitizedEncounter.mrn : 'N/A'}</TableCell>}
                    <TableCell sx={{ fontSize: '0.8rem' }}>{(sanitizedEncounter.fin && typeof sanitizedEncounter.fin === 'string') ? sanitizedEncounter.fin : 'N/A'}</TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {getAttendingPhysician(sanitizedEncounter) || ''}
                    </TableCell>
                    <TableCell>
                      {renderStatusChip(sanitizedEncounter.status)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {(sanitizedEncounter.room && typeof sanitizedEncounter.room === 'string') ? sanitizedEncounter.room : (sanitizedEncounter.roomName && typeof sanitizedEncounter.roomName === 'string') ? sanitizedEncounter.roomName : (sanitizedEncounter.roomId && typeof sanitizedEncounter.roomId === 'string') ? sanitizedEncounter.roomId : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {(sanitizedEncounter.bed && typeof sanitizedEncounter.bed === 'string') ? sanitizedEncounter.bed : (sanitizedEncounter.bedName && typeof sanitizedEncounter.bedName === 'string') ? sanitizedEncounter.bedName : (sanitizedEncounter.bedId && typeof sanitizedEncounter.bedId === 'string') ? sanitizedEncounter.bedId : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 500, color: sanitizedEncounter.status === EncounterStatus.DISCHARGED || sanitizedEncounter.status === EncounterStatus.COMPLETED ? 'text.secondary' : 'primary.main' }}>
                      {calculateStayDuration(sanitizedEncounter)}
                    </TableCell>
                  </TableRow>
                );
                });
              })()}
              </TableBody>
            </Table>
          </StyledTableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            {Array.isArray(selectedEncounters) && selectedEncounters.length > 0 && (
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportSelectedRows}
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  backgroundColor: theme.palette.secondary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.secondary.dark,
                  },
                }}
              >
                Export Selected ({Array.isArray(selectedEncounters) ? selectedEncounters.length : 0})
              </Button>
            )}
            <TablePagination
              component="div"
              count={Array.isArray(sortedEncounters) ? sortedEncounters.length : 0}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </CardContent>
      </ModernCard>

      {/* Patient Selection Dialog - also available in main case */}
      <Dialog 
        open={patientDialogOpen} 
        onClose={handleClosePatientDialog} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            zIndex: 9999
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Select Patient
          <IconButton onClick={handleClosePatientDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search by name, MRN, or DOB"
            value={patientSearch}
            onChange={e => setPatientSearch(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TableContainer component={Paper} sx={{ maxHeight: 350 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>MRN</TableCell>
                  <TableCell>Date of Birth</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!Array.isArray(filteredFacilityPatients) || filteredFacilityPatients.length === 0 ? (
                  <TableRow><TableCell colSpan={3} align="center">No patients found</TableCell></TableRow>
                ) : (
                  filteredFacilityPatients.map((p) => (
                    <TableRow
                      key={p.id}
                      hover
                      selected={selectedPatientId === p.id}
                      onClick={() => handleSelectPatient(p.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                                             <TableCell>
                         {(() => {
                           if (p.firstName && p.lastName) {
                             return `${p.firstName} ${p.lastName}`;
                           } else if (p.name) {
                             return p.name;
                           } else if (p.firstName) {
                             return p.firstName;
                           } else if (p.lastName) {
                             return p.lastName;
                           }
                           return 'N/A';
                         })()}
                       </TableCell>
                      <TableCell>{p.mrn}</TableCell>
                      <TableCell>{p.dateOfBirth}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePatientDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmPatient}
            variant="contained"
            disabled={!selectedPatientId}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncounterList;
